import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DiffEditor } from '@monaco-editor/react';
import LoadingSpinner from '../LoadingSpinner';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const STATUS_CLASS = {
  added: 'bg-green-100 text-green-800',
  modified: 'bg-amber-100 text-amber-800',
  deleted: 'bg-red-100 text-red-800',
};

const GROUP_ORDER = ['skin', 'functions', 'portal'];

function badgeCount(file) {
  const parts = [];
  if (file.additions) parts.push(`+${file.additions}`);
  if (file.deletions) parts.push(`−${file.deletions}`);
  return parts.join(' ');
}

const TurnCodeTab = ({ auditId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [sideBySide, setSideBySide] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setPayload(null);
    setSelectedId(null);
    axios.get(`${API_BASE_URL}/api/admin/ai-customizations/${auditId}/code`)
      .then((response) => {
        if (cancelled) return;
        const data = response.data?.data || { files: [], counts: { total: 0 } };
        setPayload(data);
        if (data.files?.[0]) setSelectedId(data.files[0].id);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load the code this turn changed.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [auditId]);

  const files = payload?.files || [];
  const selected = files.find((file) => file.id === selectedId) || files[0] || null;
  const groups = useMemo(() => {
    return GROUP_ORDER
      .map((group) => ({
        group,
        label: files.find((file) => file.group === group)?.groupLabel || group,
        files: files.filter((file) => file.group === group),
      }))
      .filter((entry) => entry.files.length > 0);
  }, [files]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-sm text-red-700">{error}</div>
    );
  }

  if (!files.length) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-sm font-medium text-gray-900">This turn did not change any code files.</p>
        <p className="mt-2 text-sm text-gray-500 max-w-xl mx-auto">
          Skin, function, and portal files appear here when the turn saved a new version of them.
          A skill or config-only turn has nothing to diff.
        </p>
      </div>
    );
  }

  const counts = payload.counts || {};

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
        <p className="text-sm text-gray-600">
          <span className="font-medium text-gray-900">{counts.total} file{counts.total === 1 ? '' : 's'}</span>
          {counts.added ? <span className="ml-3 text-green-700">+{counts.added} added</span> : null}
          {counts.modified ? <span className="ml-3 text-amber-700">{counts.modified} changed</span> : null}
          {counts.deleted ? <span className="ml-3 text-red-700">{counts.deleted} removed</span> : null}
        </p>
        <div className="inline-flex rounded-md border border-gray-200 p-0.5 text-xs font-medium">
          <button
            type="button"
            onClick={() => setSideBySide(false)}
            className={`px-2.5 py-1 rounded ${!sideBySide ? 'bg-gray-900 text-white' : 'text-gray-600'}`}
          >
            Inline
          </button>
          <button
            type="button"
            onClick={() => setSideBySide(true)}
            className={`px-2.5 py-1 rounded ${sideBySide ? 'bg-gray-900 text-white' : 'text-gray-600'}`}
          >
            Side by side
          </button>
        </div>
      </div>
      <div className="flex min-h-[640px]">
        <aside className="w-64 shrink-0 border-r border-gray-200 overflow-y-auto bg-gray-50">
          {groups.map((entry) => (
            <div key={entry.group} className="py-2">
              <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                {entry.label}
              </p>
              {entry.files.map((file) => {
                const active = selected && selected.id === file.id;
                return (
                  <button
                    key={file.id}
                    type="button"
                    onClick={() => setSelectedId(file.id)}
                    className={`w-full text-left px-3 py-2 border-l-2 ${
                      active ? 'bg-white border-primary-600' : 'border-transparent hover:bg-white'
                    }`}
                  >
                    <span className="block text-sm text-gray-900 break-all">{file.path}</span>
                    <span className="mt-1 flex items-center gap-2">
                      <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded ${STATUS_CLASS[file.status] || STATUS_CLASS.modified}`}>
                        {file.status}
                      </span>
                      <span className="font-mono text-[11px] text-gray-500">{badgeCount(file)}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </aside>
        <div className="flex-1 min-w-0 flex flex-col">
          {selected && (
            <>
              <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-gray-100">
                <p className="font-mono text-xs text-gray-700 truncate" title={selected.path}>{selected.path}</p>
                {selected.truncated && (
                  <p className="text-xs text-amber-700 shrink-0">Showing the first 200,000 characters</p>
                )}
              </div>
              <div className="flex-1 min-h-[580px]">
                <DiffEditor
                  key={`${selected.id}:${sideBySide ? 'split' : 'inline'}`}
                  original={selected.before || ''}
                  modified={selected.after || ''}
                  language={selected.language || 'javascript'}
                  theme="vs-dark"
                  originalModelPath={`inmemory://turn-code/${encodeURIComponent(selected.id)}/before`}
                  modifiedModelPath={`inmemory://turn-code/${encodeURIComponent(selected.id)}/after`}
                  height="580px"
                  options={{
                    readOnly: true,
                    originalEditable: false,
                    renderSideBySide: sideBySide,
                    wordWrap: 'on',
                    diffWordWrap: 'on',
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    fontSize: 13,
                    automaticLayout: true,
                    renderOverviewRuler: true,
                    ignoreTrimWhitespace: false,
                    glyphMargin: true,
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TurnCodeTab;
